<template>
  <div class="player">
    <div class="player-toggle">
        <h4 :class="!playing ? 'player-title' : 'player-title is-hidden'">{{ $t('audio_guide.title') }}</h4>
        <button class="player-button" @click="togglePlaying">
            <sprite
                v-if="!playing"
                :type="'play'"
                :width="18"
                :height="18"
            />
            <sprite
                v-else
                :type="'pause'"
                :width="15"
                :height="18"
            />
            <span v-if="!playing" class="u-visually-hidden">{{ $t('audio_guide.play') }}</span>
            <span v-else class="u-visually-hidden">{{ $t('audio_guide.stop') }}</span>
        </button>
    </div>
    <transition name="slidein">
        <div class="player-track" v-show="playing">
            <div class="player-track-inner">
                <div class="player-current">{{ currentTime }}</div>
                <div class="player-timeline">
                  <input
                    type="range"
                    class="player-timeline-seeker"
                    name="seeker"
                    min="0"
                    :max="duration"
                    ref="input"
                    v-model="playbackTime"
                    @input="updateAudio"
                  />
                </div>
                <audio
                    class="player-audio"
                    ref="audio"
                    @loadedmetadata="load"
                    @ended="end"
                    @timeupdate="updateSeeker">
                    <source :src="file" type="audio/mpeg">
                </audio>
            </div>
        </div>
    </transition>
  </div>
</template>

<script>
import Sprite from '@/components/components/Sprite.vue';

export default {
  components: {
    Sprite
  },
  data() {
    return {
      playing: false,
      loaded: false,
      playbackTime: 0,
      duration: 0,
      currentTime: '0:00'
    };
  },
  computed: {
    progressStyle() {
      return `${(this.playbackTime / this.duration) * 100}%`;
    }
  },
  props: {
    file: {
      type: String,
      required: true
    }
  },
  methods: {
    togglePlaying() {
      if (this.playing === false) {
        this.playing = true;
        this.$refs.audio.play();
      } else {
        this.playing = false;
        this.$refs.audio.pause();
      }
    },
    convertTime(secs) {
      const minutes = Math.floor(secs / 60);
      const seconds = Math.floor(secs % 60);
      const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
      return `${minutes}:${returnedSeconds}`;
    },
    load() {
      if (this.$refs.audio.readyState > 0) {
        this.loaded = true;
        this.duration = Math.round(this.$refs.audio.duration);
      } else {
        throw new Error('Failed to load audio file');
      }
    },
    updateSeeker() {
      if (this.$refs.audio) {
        const seconds = this.$refs.audio.currentTime;
        this.currentTime = this.convertTime(seconds);
        this.playbackTime = Math.floor(seconds);
        this.$refs.input.style.setProperty('--seek-before-width', this.progressStyle);
      }
    },
    updateAudio() {
      const diff = Math.abs(this.playbackTime - this.$refs.audio.currentTime);
      const seconds = this.playbackTime;
      this.currentTime = this.convertTime(seconds);
      if (diff > 0.01) {
        this.$refs.audio.currentTime = seconds;
      }
    },
    end() {
      this.playing = false;
      this.$refs.audio.pause();
      this.$refs.audio.currentTime = 0;
    }
  }
};
</script>

<style lang="scss" scoped>
    .player {
        &-toggle {
            @include position(fixed, null 0 0 null);
            @include margin(null 6px 30px null);
            z-index: 4;
            @include mq($from: 640px) {
                right: calc(50vw - 320px);
            }
        }

        &-title {
            @include font('primary', 'bold');
            @include margin(null null 5px);
            font-size: rem(10px);
            text-transform: uppercase;
            transition: opacity .3s ease-in-out, transform .3s ease-in-out;
            &.is-hidden {
                opacity: 0;
                transform: translateY(10px);
            }
        }

        &-button {
            position: relative;
            z-index: 1;
            @include size(45px);
            @include margin(null auto);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background-color: $tertiary;
            border-radius: 50%;
            box-shadow: 0 3px 6px rgba($black, .2);

            .svg {
                fill: $secondary;
                &.play {
                    @include margin(null null null 2px);
                }
            }
        }

        &-audio {
          display: none;
        }

        &-track {
            @include position(fixed, null 0 0 0);
            @include size(100%,91px);
            z-index: 3;
            max-width: 640px;
            margin: 0 auto;
            overflow: hidden;
            backdrop-filter: blur(20px);

            &-inner {
              height: 100%;
              @include padding(30px 20px null);
              display: flex;
            }
        }
        &-current {
            @include font('primary', 'bold');
            width: 40px;
            font-size: rem(10px);
        }
        &-timeline {
            position: relative;
            width: calc(100% - 105px);
            &-seeker {
              position: relative;
              -webkit-appearance: none;
              width: 100%;
              margin: 0;
              padding: 0;
              height: 9px;
              outline: none;
              background: transparent;
              cursor: pointer;
              &::-webkit-slider-runnable-track {
                width: 100%;
                height: 2px;
                cursor: pointer;
                background: rgba($tertiary, .3);
              }
              &:before {
                position: absolute;
                content: "";
                top: 4px;
                left: 0;
                width: var(--seek-before-width);
                height: 2px;
                background-color: $secondary;
                cursor: pointer;
              }
              &::-webkit-slider-thumb {
                position: relative;
                -webkit-appearance: none;
                box-sizing: content-box;
                border: none;
                @include size(9px);
                border-radius: 50%;
                background-color: $tertiary;
                cursor: pointer;
                margin: -4px 0 0 0;
                box-shadow: 0 0 0 9px rgba($tertiary,.2);
                transition: transform .1s $ease-out-quad;
              }
              &:active::-webkit-slider-thumb,
              &:focus::-webkit-slider-thumb,
              &:hover::-webkit-slider-thumb {
                transform: scale(1.5);
              }
              &::-moz-range-track {
                width: 100%;
                height: 2px;
                cursor: pointer;
                background: rgba($tertiary,.3);
              }
              &::-moz-range-progress {
                background-color: $secondary;
              }
              &::-moz-focus-outer {
                border: 0;
              }
              &::-moz-range-thumb {
                box-sizing: content-box;
                border: none;
                @include size(9px);
                border-radius: 50%;
                background-color: $tertiary;
                cursor: pointer;
                box-shadow: 0 0 0 9px rgba($tertiary,.2);
                transition: transform .1s $ease-out-quad;
              }
              &:active::-moz-range-thumb,
              &:focus::-moz-range-thumb,
              &:hover::-moz-range-thumb {
                transform: scale(1.5);
              }
              &::-ms-track {
                width: 100%;
                height: 2px;
                cursor: pointer;
                background: transparent;
                border: solid transparent;
                color: transparent;
              }
              &::-ms-fill-lower {
                background-color: $secondary;
              }
              &::-ms-fill-upper {
                background: rgba($tertiary,.3);
              }
              &::-ms-thumb {
                box-sizing: content-box;
                border: none;
                @include size(9px);
                border-radius: 50%;
                background-color: $tertiary;
                cursor: pointer;
                transition: transform .1s $ease-out-quad;
              }
              &:active::-ms-thumb,
              &:focus::-ms-thumb,
              &:hover::-ms-thumb {
                transform: scale(1.5);
              }
            }
        }
    }

    .slidein-enter-active, .slidein-leave-active {
        transition: opacity .4s $ease-out-quad;
        .player-track-inner {
            transition: transform .3s $ease-out-quad;
        }
    }
    .slidein-enter, .slidein-leave-to {
        opacity: 0;
        .player-track-inner {
            transform: translateX(100%);
        }
    }
</style>
